import dayjs from 'dayjs'
import { computed, makeObservable, observable, action, toJS } from 'mobx'

import type { Model } from '..'

export type Feature = {
  name: string
  usageIncluded?: number
}

export interface AddonDataRaw {
  id: string
  orgId: string
  name: string
  enabled: boolean
  features: Feature[]
  trialStartedAt: number | null
  trialExpiresAt: number | null
  willTrialRenew: boolean
  trialStatus: TrialStatus
}

export type TrialStatus = 'available' | 'in-progress' | 'completed'

export class Addon implements Model {
  private raw: AddonDataRaw

  get id(): string {
    return this.raw.id
  }

  get orgId(): string {
    return this.raw.orgId
  }

  get enabled(): boolean {
    return this.raw.enabled
  }

  get trialStartedAt(): number | null {
    return this.raw.trialStartedAt
  }

  // This will be renamed to trialExpiresAt, which is what it actually represents.
  get trialExpiresAt(): number | null {
    return this.raw.trialExpiresAt
  }

  get willTrialRenew(): boolean {
    return this.raw.willTrialRenew
  }

  get features(): Feature[] {
    return this.raw.features
  }

  get trialStatus(): TrialStatus | undefined {
    return this.raw.trialStatus ?? undefined
  }

  get trialDaysRemaining(): number {
    if (!this.trialExpiresAt) {
      return 0
    }

    const daysRemaining = Math.ceil(
      dayjs(this.trialExpiresAt).diff(dayjs(), 'days', true),
    )
    const clampedDaysLeft = Math.min(Math.max(daysRemaining, 0), 7)
    return clampedDaysLeft
  }

  constructor(attrs: AddonDataRaw) {
    this.raw = attrs

    makeObservable<this, 'raw' | 'localUpdate'>(this, {
      raw: observable.deep,
      id: computed,
      orgId: computed,
      enabled: computed,
      trialStartedAt: computed,
      trialExpiresAt: computed,
      willTrialRenew: computed,
      trialStatus: computed,
      trialDaysRemaining: computed,
      features: computed,
      localUpdate: action.bound,
    })
  }

  localUpdate(partial: Partial<AddonDataRaw>): this {
    this.raw = { ...this.raw, ...partial }
    return this
  }

  deserialize(json: AddonDataRaw): this {
    this.raw = json
    return this
  }

  serialize(): AddonDataRaw {
    return toJS(this.raw)
  }
}
